import { FC, Key, useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, Modal, Row } from 'antd';
import type { TreeProps } from 'antd/es/tree';
import { TbChevronRight, TbDeviceFloppy } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { RolePermissionInput } from '@/services/database';
import { LearnMoreFooter } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useRoleStore } from '@/stores/database/entities/00-RBAC/04-Roles';
import {
    MemberPermissionType,
    PermissionAction,
    PermissionScope,
    PermissionSubject,
    RolePermission,
    SetStateMethod,
} from '@/types';

import type { RoleListEntry } from '../../..';
import RolePolicyTree from '../../../components/RolePolicyTree';
import {
    getRolePermissionsForKeys,
    getRolePolicyKeysForPermissions,
    isRolePolicyKey,
} from '../../../components/RolePolicyTree/rolePolicies';

interface ViewRolePermissionsModalProps {
    mode?: 'edit' | 'view';
    role: RoleListEntry;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const workspaceResourceSubjects = [
    PermissionSubject.FOLDER,
    PermissionSubject.INVESTMENT,
    PermissionSubject.MEMO,
    PermissionSubject.PIPELINE,
    PermissionSubject.DASHBOARD,
    PermissionSubject.LIST,
    PermissionSubject.TEAM,
] as const;

function getSystemPermissions(type?: MemberPermissionType): RolePermissionInput[] {
    switch (type) {
        case MemberPermissionType.OWNER:
            return [
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.ALL,
                    action: PermissionAction.MANAGE,
                },
            ];

        case MemberPermissionType.ADMIN:
            return [
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.ALL,
                    action: PermissionAction.READ,
                },
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.WORKSPACE,
                    action: PermissionAction.UPDATE,
                },
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.USER,
                    action: PermissionAction.INVITE,
                },
                ...workspaceResourceSubjects.map((subject) => ({
                    scope: PermissionScope.WORKSPACE,
                    subject,
                    action: PermissionAction.CREATE,
                })),
            ];

        case MemberPermissionType.MEMBER:
            return [
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.WORKSPACE,
                    action: PermissionAction.READ,
                },
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.USER,
                    action: PermissionAction.READ,
                },
                ...workspaceResourceSubjects.map((subject) => ({
                    scope: PermissionScope.WORKSPACE,
                    subject,
                    action: PermissionAction.CREATE,
                })),
            ];

        case MemberPermissionType.GUEST:
        default:
            return [
                {
                    scope: PermissionScope.WORKSPACE,
                    subject: PermissionSubject.WORKSPACE,
                    action: PermissionAction.READ,
                },
            ];
    }
}

function normalizePermissions(role: RoleListEntry): RolePermissionInput[] {
    if (role.role?.permissions?.length) {
        return role.role.permissions.map((permission: RolePermission) => ({
            scope: permission.scope,
            subject: permission.subject,
            action: permission.action,
            constraints: permission.constraints,
        }));
    }

    return getSystemPermissions(role.type);
}

const ViewRolePermissionsModal: FC<ViewRolePermissionsModalProps> = ({
    mode = 'view',
    role,
    open,
    setOpen,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { updateRole } = useRoleStore();
    const initialCheckedKeys = useMemo(() => getRolePolicyKeysForPermissions(normalizePermissions(role)), [role]);
    const [checkedKeys, setCheckedKeys] = useState<Key[]>(initialCheckedKeys);
    const [name, setName] = useState(role.label);
    const [submitting, setSubmitting] = useState(false);
    const editable = mode === 'edit' && !role.system && Boolean(role.role);

    useEffect(() => {
        if (!open) {
            return;
        }

        setCheckedKeys(initialCheckedKeys);
        setName(role.label);
    }, [initialCheckedKeys, open, role.label]);

    const onCheck: TreeProps['onCheck'] = (checked) => {
        setCheckedKeys(Array.isArray(checked) ? checked : checked.checked);
    };

    const handleSave = async () => {
        if (!editable || !role.role) {
            return;
        }

        const trimmedName = name.trim();
        const selectedPolicyKeys = checkedKeys.map(String).filter(isRolePolicyKey);

        if (!trimmedName) {
            toast.error('Enter a Role Name');
            return;
        }

        if (selectedPolicyKeys.length === 0) {
            toast.error('Select at Least One Permission');
            return;
        }

        try {
            setSubmitting(true);
            await updateRole(role.role.id, {
                name: trimmedName,
                permissions: getRolePermissionsForKeys(selectedPolicyKeys),
            });
            toast('Role Updated', {
                icon: <TbDeviceFloppy size='1rem' />,
                description: trimmedName,
            });
            setOpen(false);
        } catch {
            toast.error('Unable to Update Role');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            mask={false}
            open={open}
            title={
                <>
                    <span style={{ fontWeight: 400 }}>{editable ? 'Edit Role' : 'View Permissions'}</span>
                    <span style={{ color: token.colorTextTertiary }}>
                        <TbChevronRight style={{ verticalAlign: '-0.15rem', margin: '0 0.25rem' }} />
                        <span>{role.label}</span>
                    </span>
                </>
            }
            footer={
                <Row align='middle' wrap={false}>
                    <Col flex='none'>
                        <LearnMoreFooter
                            align='left'
                            message={<>Learn more about roles in your workspace by visiting</>}
                        />
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        {editable ? (
                            <>
                                <Button onClick={() => setOpen(false)}>Cancel</Button>
                                <Button
                                    loading={submitting}
                                    type='primary'
                                    style={{ marginLeft: '0.5rem' }}
                                    onClick={() => void handleSave()}
                                >
                                    Save Changes
                                    <TbDeviceFloppy size='1rem' />
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => setOpen(false)}>Close</Button>
                        )}
                    </Col>
                </Row>
            }
            onCancel={() => setOpen(false)}
            style={{ minWidth: '55rem' }}
        >
            <div style={{ marginTop: '0.375rem' }}>
                {editable ? (
                    <Form layout='vertical' requiredMark={false}>
                        <Form.Item label='Role Name' style={{ marginBottom: '0.5rem' }}>
                            <Input value={name} onChange={(event) => setName(event.target.value)} />
                        </Form.Item>
                    </Form>
                ) : null}
                <RolePolicyTree
                    checkedKeys={checkedKeys}
                    minHeight='25rem'
                    maxHeight='35rem'
                    onCheck={onCheck}
                    readOnly={!editable}
                />
            </div>
        </Modal>
    );
};

export default ViewRolePermissionsModal;
